import React, { useState, useEffect } from 'react';
import { X, Filter, Check, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';

const RoleFilterModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  selectedRoles = [],
  roles = []
}) => {
  const [localSelected, setLocalSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalSelected([...selectedRoles]);
      setSearchQuery('');
    }
  }, [isOpen, selectedRoles]);

  if (!isOpen) return null;

  const filteredRoles = roles.filter(role =>
    role.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (roleValue) => {
    setLocalSelected(prev => {
      if (prev.includes(roleValue)) {
        return prev.filter(r => r !== roleValue);
      } else {
        return [...prev, roleValue];
      }
    });
  };

  const handleSelectAll = () => {
    if (localSelected.length === filteredRoles.length) {
      setLocalSelected([]);
    } else {
      setLocalSelected(filteredRoles.map(r => r.id || r.name));
    }
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    onApply(localSelected);
    onClose();
  };

  const handleReset = () => {
    setLocalSelected([...selectedRoles]);
  };

  const allSelected = localSelected.length === filteredRoles.length && filteredRoles.length > 0;
  const noneSelected = localSelected.length === 0;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Filter by Role
              </h2>
              <p className="text-sm text-gray-500">
                Select roles to filter
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {/* Search */}
          <div className="relative">
            <Input
              placeholder="Search roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Filter className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="text-sm"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700"
              disabled={noneSelected}
            >
              Clear All
            </Button>
          </div>

          {/* Role Options */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredRoles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No roles found</p>
              </div>
            ) : (
              filteredRoles.map((role) => {
                const roleValue = role.id || role.name;
                const isSelected = localSelected.includes(roleValue);
                
                return (
                  <div
                    key={roleValue}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                    onClick={() => handleToggle(roleValue)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggle(roleValue)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{role.name}</p>
                        {role.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="ml-2">
                        <Check className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Selected Count */}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {localSelected.length === 0 ? (
                'No roles selected'
              ) : localSelected.length === filteredRoles.length ? (
                'All roles selected'
              ) : (
                `${localSelected.length} of ${filteredRoles.length} roles selected`
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={JSON.stringify(localSelected) === JSON.stringify(selectedRoles)}
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            disabled={localSelected.length === 0}
          >
            Apply Filter
            {localSelected.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {localSelected.length}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoleFilterModal;

